'use client';
import PhoneInput from '@/components/(dashboard)/login-register/PhoneInput';
import { mutate } from 'swr';
import OtpInput from '@/components/(dashboard)/login-register/OtpInput';
import TransitionWrapper from '@/components/(dashboard)/login-register/TransitionWrapper';
import { useRouter } from 'next/navigation';
import { useToast } from '@/hooks/use-toast';
import { handleSendOtp, handleVerifyOtp } from '@/utils/profile/login-register/loginUtils';
import { useErrorStore } from '@/stores/profile/errorStore';
import React, { useCallback, useEffect, useState } from 'react';
import useAuthStore from '@/stores/profile/authStore';
import NameInput from '@/components/(dashboard)/login-register/NameInput';
import { updateUser } from '@/utils/profile/updateUser';
import useVerifyUser from '@/hooks/profile/useVerifyUser';
import { useSurveyStore } from '@/stores/survey/store';

interface PhoneNumberModalProps {
    isModalOpen?: boolean;
    setIsModalOpen?: ((value: boolean) => void) | undefined;
    redirect?: string;
    handleLoginSuccess?: () => void;
    title?: string;
    textBtn?: string;
    landingSlug?: string;
    setCurrentStep?: (step: number) => void;
    customHeight?: string;
    httpReferrer?: string;
    newStyle?: boolean;
    selectedWizard?: string | undefined;
    otpBtnText?: string;
    popupWizardStyle?: boolean;
}

const LoginFormComponent: React.FC<PhoneNumberModalProps> = ({
    setIsModalOpen,
    isModalOpen,
    redirect,
    handleLoginSuccess,
    title,
    textBtn,
    landingSlug,
    setCurrentStep,
    customHeight,
    httpReferrer,
    newStyle,
    selectedWizard,
    otpBtnText,
    popupWizardStyle,
}) => {
    const [step, setStep] = useState(1);
    const { setRegistrationStep } = useSurveyStore();

    useEffect(() => {
        setRegistrationStep(step);
    }, [step, setRegistrationStep]);
    const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
    const { isLoggedInGlobal, user } = useVerifyUser();
    const setIsLoggedIn = useAuthStore((s) => s.setIsLoggedIn);
    const setIsShebaLoading = useAuthStore((s) => s.setIsShebaLoading);
    const [loading, setLoading] = useState(false);
    const [phone, setPhone] = useState<string>(user?.mobile ?? '');
    const [fullName, setFullName] = useState<string>(user?.firstName ?? '');
    const [email, setEmail] = useState<string>(user?.email ?? '');
    const [, setPendingOtp] = useState('');
    const [, setToken] = useState('');
    const router = useRouter();
    const { error, clearError } = useErrorStore();
    const { toast } = useToast();
    const [retryTime, setRetryTime] = useState(60);

    // your custom condition
    const needNameAfterOtp =
        popupWizardStyle ||
        selectedWizard === 'wizard2' ||
        landingSlug === 'nokhbegan' ||
        landingSlug === 'nokhbegan-main';

    useEffect(() => {
        if (isLoggedInGlobal) {
            setPhone(user?.mobile ?? '');
            setEmail(user?.email ?? '');
            setFullName(user?.firstName ?? '');
        }
    }, [isLoggedInGlobal, user]);
    useEffect(() => {
        if (retryTime === 0) return;
        if (step === 1) setRetryTime(60);
        const t = setInterval(() => {
            setRetryTime((prev) => {
                if (prev <= 1) {
                    clearInterval(t);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(t);
    }, [retryTime, step]);

    const handleRetry = () => {
        if (retryTime > 0 || step !== 2) return;
        setRetryTime(60);
        if (phone) handleSendOtp(phone, setPhone, setStep, setLoading, landingSlug, httpReferrer);
    };

    const onLoginSuccess = useCallback(() => {
        if (setCurrentStep) setCurrentStep(1);
        if (handleLoginSuccess) handleLoginSuccess();
        toast({
            className: 'bg-primary dark:bg-white text-white dark:text-primary rounded-2xl',
            title: 'به خانه سرمایه خوش آمدید!',
            duration: 500,
        });
        if (setIsModalOpen) {
            setTimeout(() => {
                setIsModalOpen(false);
                if (redirect) router.push(redirect);
            }, 2000);
        }
        setIsLoggedIn(true);
        setIsShebaLoading(true);
        mutate('verifyUser', undefined, { revalidate: true });
    }, [
        setCurrentStep,
        handleLoginSuccess,
        toast,
        setIsModalOpen,
        setIsLoggedIn,
        setIsShebaLoading,
        redirect,
        router,
    ]);

    const handleEditPhone = () => {
        setStep(1);
        setPhone('');
        setRetryTime(0);
    };

    useEffect(() => {
        if (error) {
            toast({ className: 'bg-red-800 text-white rounded-2xl', title: error });
            clearError();
        }
    }, [error, toast, clearError]);

    useEffect(() => {
        if (isLoggedIn && isModalOpen) onLoginSuccess();
    }, [onLoginSuccess, isLoggedIn, isModalOpen]);

    // === OTP verify: always verify; if needNameAfterOtp then go to step 3 on success
    const verifyOtpCallback = useCallback(
        async (otp: string) => {
            setLoading(true);
            try {
                const result = await handleVerifyOtp(
                    phone,
                    otp,
                    setToken,
                    setLoading,
                    landingSlug,
                    httpReferrer,
                );

                if (result?.success && needNameAfterOtp) {
                    setPendingOtp(otp);
                    // Revalidate user before showing step 3 so fullName/email are populated
                    const freshData = await mutate('verifyUser');
                    const freshUser = freshData?.data?.user;
                    if (freshUser) {
                        setFullName(freshUser.firstName ?? '');
                        setEmail(freshUser.email ?? '');
                    }
                    setStep(3);
                }

                return result;
            } finally {
                setLoading(false);
            }
        },
        [phone, landingSlug, httpReferrer, needNameAfterOtp],
    );

    // OtpInput's onSuccess: only used when needNameAfterOtp === false
    const handleOtpSuccess = useCallback(() => {
        if (!needNameAfterOtp) {
            onLoginSuccess();
        }
    }, [needNameAfterOtp, onLoginSuccess]);

    // Step 3 submit: update profile (fullName + email) AFTER successful OTP verification
    const handleNameSubmit = useCallback(
        async (nameFromChild: string) => {
            setLoading(true);
            try {
                setFullName(nameFromChild);
                setEmail(email);
                const res = await updateUser({ fullName: nameFromChild, email });
                if (!res || !res.success) {
                    // updateUser already showed a toast; keep user on this step
                    return;
                }
                // only now proceed
                onLoginSuccess();
            } finally {
                setLoading(false);
            }
        },
        [email, onLoginSuccess],
    );

    const containerHeightClass = popupWizardStyle ? 'h-auto' : customHeight;

    return (
        <div
            className={`flex flex-col items-start justify-center relative w-full rounded-3xl ${popupWizardStyle ? 'bg-transparent ' + containerHeightClass : 'bg-white dark:bg-primary-dark ' + containerHeightClass}`}
        >
            <TransitionWrapper show={step === 1}>
                <PhoneInput
                    textBtn={textBtn}
                    title={title}
                    newStyle={newStyle}
                    landingSlug={landingSlug}
                    popupWizardStyle={popupWizardStyle}
                    onSubmit={(phone) =>
                        handleSendOtp(
                            phone,
                            setPhone,
                            setStep,
                            setLoading,
                            landingSlug,
                            httpReferrer,
                        )
                    }
                    loading={loading}
                />
            </TransitionWrapper>

            <TransitionWrapper show={step === 2}>
                <OtpInput
                    selectedWizard={needNameAfterOtp}
                    popup
                    phone={phone}
                    newStyle={newStyle}
                    otpBtnText={otpBtnText}
                    popupWizardStyle={popupWizardStyle}
                    onVerify={verifyOtpCallback} // 👈 skips API when needNameAfterOtp
                    onEditPhone={handleEditPhone}
                    loading={loading}
                    onSuccess={handleOtpSuccess} // finishes only when needNameAfterOtp === false
                    retryTime={retryTime}
                    onRetry={handleRetry}
                />
            </TransitionWrapper>

            <TransitionWrapper show={step === 3}>
                <NameInput
                    email={email}
                    setEmail={setEmail}
                    value={fullName}
                    onChange={setFullName}
                    onSubmit={handleNameSubmit}
                    loading={loading}
                    textBtn='ادامه'
                    newStyle={newStyle}
                    popupWizardStyle={popupWizardStyle}
                />
            </TransitionWrapper>
        </div>
    );
};

export default LoginFormComponent;
